import type { FC } from "react";
import { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Table,
  TextInput,
  Select,
  Alert,
  ModalHeader,
  ModalBody,
  ModalFooter,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
} from "flowbite-react";
import { HiPencilAlt, HiTrash } from "react-icons/hi";
import type {
  Commission,
  CreateCommissionData,
} from "../../services/commissions";
import {
  createCommission,
  deleteCommission,
  updateCommission,
} from "../../services/commissions";
import { getCommissionLevels } from "../../services/commission-levels";
import type { CommissionLevel } from "../../types/commission-level";

interface EditingCommission extends Omit<Commission, "commission_level_id" | "comission"> {
  commission_level_id: string | number;
  comission: string | number;
}

interface CommissionsModalProps {
  show: boolean;
  onClose: () => void;
  contractTypeId: number;
  commissions: Commission[];
  onUpdate: () => Promise<void>;
}

const CommissionsModal: FC<CommissionsModalProps> = ({
  show,
  onClose,
  contractTypeId,
  commissions,
  onUpdate,
}) => {
  const [levels, setLevels] = useState<CommissionLevel[]>([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newCommission, setNewCommission] = useState({
    commission_level_id: "",
    comission: "",
    valid_from: "",
    valid_to: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editingCommission, setEditingCommission] =
    useState<EditingCommission | null>(null);

  useEffect(() => {
    const loadLevels = async () => {
      try {
        const levelsData = await getCommissionLevels();
        setLevels(levelsData);
      } catch (error) {
        console.error("Failed to load commission levels:", error);
      }
    };
    loadLevels();
  }, []);

  const validateCommission = (commission: {
    commission_level_id: string | number;
    comission: string | number;
  }) => {
    if (!commission.commission_level_id) {
      setError("Please select a commission level");
      return false;
    }
    if (!commission.comission) {
      setError("Please enter a commission value");
      return false;
    }
    if (
      isNaN(parseFloat(commission.comission.toString())) ||
      parseFloat(commission.comission.toString()) < 0
    ) {
      setError("Please enter a valid commission value");
      return false;
    }
    return true;
  };

  const handleAddCommission = async () => {
    setError(null);
    setSuccessMessage(null);

    if (!validateCommission(newCommission)) return;

    setIsSubmitting(true);
    try {
      const currentDate = new Date().toISOString().split("T")[0];
      // Ensure all fields are properly typed before creating commission data
      const validFrom = newCommission.valid_from || currentDate;
      const commissionData: CreateCommissionData = {
        contract_type_id: contractTypeId,
        commission_level_id: parseInt(newCommission.commission_level_id.toString()),
        comission: parseFloat(newCommission.comission.toString()),
        valid_from: validFrom || "", // Ensure valid_from is always a string
        valid_to: newCommission.valid_to || null,
      };

      const result = await createCommission(commissionData);

      if (!result.success)
        throw new Error(result.error || "Failed to create commission");

      setSuccessMessage("Commission added successfully");
      await onUpdate();
      setIsAddingNew(false);
      setNewCommission({
        commission_level_id: "",
        comission: "",
        valid_from: "",
        valid_to: "",
      });
    } catch (error) {
      console.error("Error creating commission:", error);
      setError(
        error instanceof Error ? error.message : "Failed to create commission"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCommission = async () => {
    if (!editingCommission) return;

    setError(null);
    setSuccessMessage(null);

    if (!validateCommission(editingCommission)) return;

    setIsSubmitting(true);
    try {
      const result = await updateCommission(editingCommission.id, {
        commission_level_id:
          typeof editingCommission.commission_level_id === "string"
            ? parseInt(editingCommission.commission_level_id)
            : editingCommission.commission_level_id,
        comission:
          typeof editingCommission.comission === "string"
            ? parseFloat(editingCommission.comission)
            : editingCommission.comission,
        valid_from: editingCommission.valid_from,
        valid_to: editingCommission.valid_to || null,
      });

      if (!result.success) {
        throw new Error(result.error || "Failed to update commission");
      }

      setSuccessMessage("Provision aktualisiert.");
      await onUpdate();
      setEditingCommission(null);
    } catch (error) {
      console.error("Fehler beim aktualisieren:", error);
      setError(
        error instanceof Error ? error.message : "Fehler beim aktualisieren."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = (commission: Commission) => {
    setEditingCommission({
      ...commission,
      valid_from: commission.valid_from,
      valid_to: commission.valid_to,
    });
    setError(null);
    setSuccessMessage(null);
  };

  const handleCancelEdit = () => {
    setEditingCommission(null);
    setError(null);
    setSuccessMessage(null);
  };

  const handleDeleteCommission = async (id: number) => {
    const result = await deleteCommission(id);
    if (result.success) {
      await onUpdate();
    } else {
      console.error("Fehler beim aktualisieren:", result.error);
    }
  };

  return (
    <Modal size="7xl" onClose={onClose} show={show}>
      <ModalHeader className="border-b border-gray-200 !p-8 dark:border-gray-700">
        <strong>Manage Commissions</strong>
      </ModalHeader>
      <ModalBody className="p-8">
        {error && (
          <Alert color="failure" className="mb-4">
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert color="success" className="mb-4">
            {successMessage}
          </Alert>
        )}
        <div className="space-y-6">
          <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
            <TableHead className="bg-gray-100 dark:bg-gray-700">
              <TableHeadCell>Level</TableHeadCell>
              <TableHeadCell>Commission</TableHeadCell>
              <TableHeadCell>Valid From</TableHeadCell>
              <TableHeadCell>Valid To</TableHeadCell>
              <TableHeadCell>Actions</TableHeadCell>
            </TableHead>
            <TableBody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {commissions.map((commission) =>
                editingCommission?.id === commission.id ? (
                  <TableRow
                    key={commission.id}
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <TableCell className="whitespace-nowrap p-6">
                      <Select
                        value={editingCommission.commission_level_id}
                        onChange={(e) =>
                          setEditingCommission({
                            ...editingCommission,
                            commission_level_id: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Level</option>
                        {levels.map((level) => (
                          <option key={level.id} value={level.id}>
                            Level {level.level_number} - {level.description}
                          </option>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-6">
                      <TextInput
                        type="number"
                        step="0.01"
                        value={editingCommission.comission}
                        onChange={(e) =>
                          setEditingCommission({
                            ...editingCommission,
                            comission: e.target.value,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-6">
                      <TextInput
                        type="date"
                        value={editingCommission.valid_from}
                        onChange={(e) =>
                          setEditingCommission({
                            ...editingCommission,
                            valid_from: e.target.value,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-6">
                      <TextInput
                        type="date"
                        value={editingCommission.valid_to || ""}
                        onChange={(e) =>
                          setEditingCommission({
                            ...editingCommission,
                            valid_to: e.target.value || null,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell className="space-x-2 whitespace-nowrap p-6">
                      <div className="flex items-center gap-x-3">
                        <Button
                          color="primary"
                          size="sm"
                          onClick={handleEditCommission}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Saving..." : "Save"}
                        </Button>
                        <Button
                          color="gray"
                          size="sm"
                          onClick={handleCancelEdit}
                        >
                          Abbrechen
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow
                    key={commission.id}
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <TableCell className="whitespace-nowrap p-6 text-base font-normal text-gray-500 dark:text-gray-400">
                      Level {commission.level_number} - {commission.level_description}
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-6 text-base font-medium text-gray-900 dark:text-white">
                      {commission.comission}
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-6 text-base font-medium text-gray-900 dark:text-white">
                      {new Date(commission.valid_from).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-6 text-base font-medium text-gray-900 dark:text-white">
                      {commission.valid_to
                        ? new Date(commission.valid_to).toLocaleDateString()
                        : "Kein Enddatum"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-6">
                      <div className="flex items-center gap-x-3">
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => handleStartEdit(commission)}
                        >
                          <HiPencilAlt className="mr-2 text-lg" />
                          Bearbeiten
                        </Button>
                        <Button
                          color="failure"
                          size="sm"
                          onClick={() => handleDeleteCommission(commission.id)}
                        >
                          <HiTrash className="mr-2 text-lg" />
                          Löschen
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              )}
              {isAddingNew && (
                <TableRow className="hover:bg-gray-100 dark:hover:bg-gray-700">
                  <TableCell className="whitespace-nowrap p-6">
                    <Select
                      value={newCommission.commission_level_id}
                      onChange={(e) =>
                        setNewCommission({
                          ...newCommission,
                          commission_level_id: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Level</option>
                      {levels.map((level) => (
                        <option key={level.id} value={level.id}>
                          Level {level.level_number} - {level.description}
                        </option>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell className="whitespace-nowrap p-6">
                    <TextInput
                      type="number"
                      step="0.01"
                      value={newCommission.comission}
                      onChange={(e) =>
                        setNewCommission({
                          ...newCommission,
                          comission: e.target.value,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap p-6">
                    <TextInput
                      type="date"
                      value={newCommission.valid_from}
                      onChange={(e) =>
                        setNewCommission({
                          ...newCommission,
                          valid_from: e.target.value,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap p-6">
                    <TextInput
                      type="date"
                      value={newCommission.valid_to}
                      onChange={(e) =>
                        setNewCommission({
                          ...newCommission,
                          valid_to: e.target.value,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell className="space-x-2 whitespace-nowrap p-6">
                    <div className="flex items-center gap-x-3">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={handleAddCommission}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Saving..." : "Save"}
                      </Button>
                      <Button
                        color="gray"
                        size="sm"
                        onClick={() => setIsAddingNew(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!isAddingNew && (
            <div className="flex justify-end">
              <Button color="primary" onClick={() => setIsAddingNew(true)}>
                Add Commission
              </Button>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="p-8">
        <Button color="gray" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CommissionsModal;
